import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useLocation } from 'wouter';
import { useConvertLead, type Lead } from '@/models/leads';

export function ConvertLeadModal({
  lead,
  opened,
  onClose,
}: {
  lead: Lead;
  opened: boolean;
  onClose: () => void;
}) {
  const [, setLocation] = useLocation();
  const convert = useConvertLead();
  const form = useForm({
    initialValues: {
      firstName: lead.firstName ?? '',
      lastName: lead.lastName ?? '',
      email: lead.email ?? '',
      phone: lead.phone ?? '',
      whatsapp: lead.whatsapp ?? '',
      telegram: lead.telegram ?? '',
      notes: lead.extendedMessage ?? '',
    },
    validate: {},
  });

  const derivedAccountName = (() => {
    const composed = [form.values.firstName, form.values.lastName]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' ');
    return (
      composed || form.values.email || form.values.phone || 'Unnamed account'
    );
  })();

  const submit = form.onSubmit(async (values) => {
    if (!values.email && !values.phone) {
      form.setFieldError('email', 'email or phone required');
      form.setFieldError('phone', 'email or phone required');
      return;
    }
    const { contact } = await convert.mutateAsync({
      leadId: lead.id,
      payload: values,
    });
    onClose();
    setLocation(`~/contacts/${contact.id}`);
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Convert lead to contact"
      size="lg"
    >
      <form onSubmit={submit}>
        <Stack>
          <Alert color="blue" variant="light">
            <Text size="sm">
              Once converted, this lead is locked for further changes. All
              future communication should be tracked on the resulting contact
              and offers.
            </Text>
            <Text size="sm" mt={6}>
              A new account named <b>{derivedAccountName}</b> will be created.
            </Text>
          </Alert>
          <Group grow>
            <TextInput
              label="First name"
              {...form.getInputProps('firstName')}
            />
            <TextInput label="Last name" {...form.getInputProps('lastName')} />
          </Group>
          <Group grow>
            <TextInput
              label="Email"
              type="email"
              {...form.getInputProps('email')}
            />
            <TextInput label="Phone" {...form.getInputProps('phone')} />
          </Group>
          <Group grow>
            <TextInput label="WhatsApp" {...form.getInputProps('whatsapp')} />
            <TextInput label="Telegram" {...form.getInputProps('telegram')} />
          </Group>
          <Textarea
            label="Notes (optional, on new contact)"
            autosize
            minRows={2}
            {...form.getInputProps('notes')}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={convert.isPending}>
              Convert
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
